import Image from 'next/image'
import { useState } from 'react'
import { Fade } from 'react-awesome-reveal'
import Masonry from 'react-masonry-css'

import { Breadcrumbs } from '@/components/atomic/Breadcrumbs'
import { PageContainer } from '@/components/atomic/PageContainer'
import { PageMetaData } from '@/components/atomic/PageMetaData'
import { ButtonHierarchy } from '@/components/styleguide/Button'
import { ChatWidget } from '@/components/styleguide/ChatWidget'
import { Header } from '@/components/styleguide/Header'
import { InternalLink } from '@/components/styleguide/Link'
import { ROUTES } from '@/lib/routes'
import { getPageContentListService } from '@/lib/services/pageService'
import { getGuideCategoryName, getGuideCategoryType } from '@/lib/utilities/page'
import { Size } from '@/types/system'

const breakpointColumns = {
  default: 4,
  1200: 3,
  992: 2,
  576: 1,
}

export default function GuidesCategoryPage({ guideList, pageSlug }) {
  const [breadcrumbs] = useState([
    {
      title: 'Home',
      slug: ROUTES.homePage.url,
    },
    {
      title: `Leasing Guides`,
      slug: ROUTES.guidesPage.url,
    },
    {
      title: `${getGuideCategoryName(pageSlug)}`,
      slug: null,
    },
  ])

  return (
    <>
      <PageMetaData
        title={`LeaseLoco Guide: ${getGuideCategoryName(pageSlug)}`}
        pageTitle={`LeaseLoco Guide: ${getGuideCategoryName(pageSlug)}`}
        description="Take a pit stop, grab all the latest car leasing news, industry insight and more on the LeaseLoco blog. The UK's leading car leasing comparison site."
        pageUrl={`/guides/${pageSlug}`}
      />
      <ChatWidget />
      <Header backButtonUrl={ROUTES.guidesPage.url} />
      <PageContainer containerClass="container--guides">
        <div className="row">
          <div className="col-12 text-left mb-4">
            <Breadcrumbs breadcrumbs={breadcrumbs} />
            <h1>{getGuideCategoryName(pageSlug)}</h1>
            <InternalLink
              href={ROUTES.guidesPage.url}
              hierarchy={ButtonHierarchy.Borderless}
              icon="ChevronLeft"
              size={Size.Medium}
              hasPadding
              hasBackground>
              Back to Guides
            </InternalLink>
          </div>
        </div>
        <div className="row">
          <div className="col-12">
            <Masonry
              breakpointCols={breakpointColumns}
              className="masonry-grid"
              columnClassName="masonry-grid_col">
              {guideList.items.map(item => (
                <Fade
                  key={item.id}
                  triggerOnce>
                  <div className="category__card">
                    <Image
                      src={item.content.thumbnail}
                      alt={item.content.thumbnailAlt}
                      width="255"
                      height="170"
                    />
                    <div className="category__card__content">
                      <h2>{item.content.title}</h2>
                      <p>{item.content.description}</p>
                      <InternalLink
                        href={`/guides/${pageSlug}/${item.urlKey}`}
                        size={Size.Medium}
                        hierarchy={ButtonHierarchy.Filled}
                        hasPadding
                        hasBackground>
                        Read now
                      </InternalLink>
                    </div>
                  </div>
                </Fade>
              ))}
            </Masonry>
          </div>
        </div>
      </PageContainer>
    </>
  )
}

export const getServerSideProps = async context => {
  const pageSlug = context.params.category

  try {
    const guideList = await getPageContentListService({
      type: getGuideCategoryType(pageSlug),
      page: '1',
    })

    return {
      props: {
        guideList,
        pageSlug,
      },
    }
  } catch (err) {
    return {
      notFound: true,
    }
  }
}
